import * as THREE from "three";
import { FORMATIONS, type Cloud, type FormationKey } from "./formations";
import { sampleText, sampleSVG, sampleImageFile, type RenderMode } from "./sources";

/**
 * A "source" is a serialisable description of a shape the swarm can make. Both
 * free-play (useSwarmSource) and the sequencer build clouds from these, so the
 * type + builder live here (neutral, no React) rather than inside a hook.
 */
export type LissajousParams = {
  a: number; // x frequency
  b: number; // y frequency
  c: number; // z frequency
  phase: number; // degrees, shifts x (and z) against y — morphs the knot
  depth: number; // 0 = flat 2D curve, 1 = fully 3D
};

export const DEFAULT_LISSAJOUS: LissajousParams = { a: 3, b: 2, c: 7, phase: 90, depth: 0.5 };

export type CymaticsParams = {
  m: number; // plate mode number (x)
  n: number; // plate mode number (z)
  mix: 1 | -1; // + symmetric / − antisymmetric combination
  form: "plate" | "sand"; // vibrating plane vs sand-on-nodal-lines
  span: number; // plate physical extent in mode-lengths — bigger = MORE nodes,
  //               not a zoom: the field wavelength is fixed, so a larger plate
  //               fits more full periods and the drones fill more nodal cells.
};

export const DEFAULT_CYMATICS: CymaticsParams = { m: 3, n: 5, mix: -1, form: "plate", span: 1 };

/** Side length of the square cymatics plate, scene units. */
export const PLATE_L = 5.2;

/** Live audio-visualizer styles (positions driven by the analyser each frame). */
export type VisStyle = "corona" | "ribbon" | "terrain" | "orb" | "nebula" | "tunnel";

/**
 * The visualizer style registry — the UI maps over this, so ADDING A STYLE is:
 * (1) extend `VisStyle` + add an entry here, (2) add its layout branch in
 * `VisualizerField.tsx`. Nothing else to touch.
 */
export const VIS_STYLE_LIST: Array<{ key: VisStyle; label: string; hint: string }> = [
  { key: "corona", label: "Corona", hint: "Radial spectrum sun — angle = frequency, radius = magnitude" },
  { key: "ribbon", label: "Ribbon", hint: "The live waveform wrapped around a ring" },
  { key: "terrain", label: "Terrain", hint: "A scrolling spectrogram landscape — x = frequency, z = time, height = magnitude" },
  { key: "orb", label: "Orb", hint: "An energy planet — the spectrum pushes the sphere's surface outward, pole to pole" },
  { key: "nebula", label: "Nebula", hint: "A noise cloud breathing with the bass — organic, alive" },
  { key: "tunnel", label: "Tunnel", hint: "A bass tunnel — rings shaped by the low end flow past you; fly it in FPV" },
];

export type Source =
  | { kind: "formation"; key: FormationKey }
  | { kind: "text"; text: string; font: string }
  | { kind: "svg"; name: string; svg: string }
  | { kind: "image"; name: string; url: string }
  | ({ kind: "lissajous" } & LissajousParams)
  | ({ kind: "cymatics" } & CymaticsParams)
  | { kind: "visualizer"; style: VisStyle };

const TAU = Math.PI * 2;

/**
 * Native parametric Lissajous — pure math, no rasterising. The curve is sampled
 * densely, then arc-length resampled to EXACTLY n evenly spaced points, so the
 * drones trace it like a light ribbon. Because it's a real point cloud (not an
 * image), everything emergent — neighbor physics, the audio wave field — acts
 * on the curve directly: music ripples and pulses *along* it.
 */
export function lissajousCloud(n: number, prm: LissajousParams): Cloud {
  const { a, b, c, depth } = prm;
  const ph = (prm.phase * Math.PI) / 180;
  const R = 2.6;

  // dense pass (integer frequencies close the curve at t = TAU)
  const M = Math.max(2048, n * 4);
  const dx = new Float32Array(M + 1);
  const dy = new Float32Array(M + 1);
  const dz = new Float32Array(M + 1);
  const cum = new Float32Array(M + 1);
  let len = 0;
  for (let i = 0; i <= M; i++) {
    const t = (i / M) * TAU;
    dx[i] = R * Math.sin(a * t + ph);
    dy[i] = R * Math.sin(b * t);
    dz[i] = R * depth * Math.sin(c * t + ph * 0.5);
    if (i > 0) len += Math.hypot(dx[i] - dx[i - 1], dy[i] - dy[i - 1], dz[i] - dz[i - 1]);
    cum[i] = len;
  }

  // arc-length even resample to n points, rainbow hue along the curve
  const p = new Float32Array(n * 3);
  const col = new Float32Array(n * 3);
  const hc = new THREE.Color();
  let seg = 0;
  for (let i = 0; i < n; i++) {
    const target = (i / n) * len;
    while (seg < M && cum[seg + 1] < target) seg++;
    const span = cum[seg + 1] - cum[seg] || 1;
    const f = (target - cum[seg]) / span;
    p[i * 3] = dx[seg] + (dx[seg + 1] - dx[seg]) * f;
    p[i * 3 + 1] = dy[seg] + (dy[seg + 1] - dy[seg]) * f;
    p[i * 3 + 2] = dz[seg] + (dz[seg + 1] - dz[seg]) * f;
    hc.setHSL((i / n) * 0.85, 0.85, 0.55);
    col[i * 3] = hc.r;
    col[i * 3 + 1] = hc.g;
    col[i * 3 + 2] = hc.b;
  }
  return { positions: p, colors: col };
}

/** The Chladni plate field at (u, v) ∈ [−0.5, 0.5]² for mode (m, n, mix). */
export function chladni(u: number, v: number, m: number, n: number, mix: number): number {
  return (
    Math.cos(m * Math.PI * u) * Math.cos(n * Math.PI * v) +
    mix * Math.cos(n * Math.PI * u) * Math.cos(m * Math.PI * v)
  );
}

/**
 * The plate's mode ladder, sorted by eigenfrequency (∝ m² + n²) — sweeping it
 * in order is what running a tone generator up a physical Chladni plate does.
 * The antisymmetric (−) combination is degenerate when m = n, so those pairs
 * only appear on the + ladder.
 */
export function cymaticsLadder(mix: 1 | -1): Array<{ m: number; n: number }> {
  const modes: Array<{ m: number; n: number }> = [];
  for (let m = 1; m <= 10; m++)
    for (let n = m + (mix === -1 ? 1 : 0); n <= 10; n++) modes.push({ m, n });
  modes.sort((a, b) => a.m * a.m + a.n * a.n - (b.m * b.m + b.n * b.n) || a.m - b.m);
  return modes;
}

/**
 * Cymatics — the Chladni plate as a drone cloud (horizontal, y = 0 plane).
 *
 *  - "plate": a uniform grid across the square — the wave-forming plane. The
 *    cloud is FLAT; the standing-wave oscillation happens live in the vertex
 *    shader (base physics), so antinodes pump while nodal lines hold still.
 *    The mode shape is also painted into the colors (bright antinodes, dark
 *    nodal lines), so the pattern reads even in neighbor mode / flat.
 *  - "sand": drones gather ON the nodal lines (marching-squares contour of
 *    the zero set, evenly distributed by arc length) — the iconic cymatics
 *    figure. During a sweep with neighbor physics they flow from pattern to
 *    pattern exactly like sand rearranging.
 */
export function cymaticsCloud(count: number, prm: CymaticsParams): Cloud {
  const { m, n, mix, span } = prm;
  const p = new Float32Array(count * 3);
  const col = new Float32Array(count * 3);
  const hc = new THREE.Color();

  const paintPlate = () => {
    const side = Math.max(2, Math.ceil(Math.sqrt(count)));
    const d = side - 1;
    for (let i = 0; i < count; i++) {
      const u = ((i % side) / d - 0.5) * span;
      const v = (Math.floor(i / side) / d - 0.5) * span;
      p[i * 3] = u * PLATE_L;
      p[i * 3 + 1] = 0;
      p[i * 3 + 2] = v * PLATE_L;
      const c = chladni(u, v, m, n, mix) * 0.5; // ≈ −1..1
      const a = Math.min(1, Math.abs(c));
      hc.setHSL(0.55 - c * 0.16, 0.85, 0.18 + a * 0.5); // nodal lines dark, antinodes bright
      col[i * 3] = hc.r; col[i * 3 + 1] = hc.g; col[i * 3 + 2] = hc.b;
    }
    return { positions: p, colors: col };
  };

  if (prm.form === "plate") return paintPlate();

  // ---- sand: marching-squares zero contour, evenly resampled ----
  const G = 200;
  const F = new Float32Array((G + 1) * (G + 1));
  for (let j = 0; j <= G; j++)
    for (let i = 0; i <= G; i++)
      F[j * (G + 1) + i] = chladni((i / G - 0.5) * span, (j / G - 0.5) * span, m, n, mix);

  const segs: number[] = []; // [ax, az, bx, bz, ...] in plate uv space
  const zc = (a: number, b: number) => a / (a - b); // zero crossing along an edge
  for (let j = 0; j < G; j++) {
    for (let i = 0; i < G; i++) {
      const f00 = F[j * (G + 1) + i], f10 = F[j * (G + 1) + i + 1];
      const f01 = F[(j + 1) * (G + 1) + i], f11 = F[(j + 1) * (G + 1) + i + 1];
      const pts: number[] = [];
      if (f00 * f10 < 0) pts.push((i + zc(f00, f10)) / G - 0.5, j / G - 0.5);
      if (f10 * f11 < 0) pts.push((i + 1) / G - 0.5, (j + zc(f10, f11)) / G - 0.5);
      if (f01 * f11 < 0) pts.push((i + zc(f01, f11)) / G - 0.5, (j + 1) / G - 0.5);
      if (f00 * f01 < 0) pts.push(i / G - 0.5, (j + zc(f00, f01)) / G - 0.5);
      if (pts.length >= 4) segs.push(pts[0], pts[1], pts[2], pts[3]);
      if (pts.length === 8) segs.push(pts[4], pts[5], pts[6], pts[7]);
    }
  }

  const nSegs = segs.length / 4;
  if (nSegs < 2) return paintPlate(); // degenerate mode (e.g. m = n with −): fall back

  // cumulative length over all segments, then walk it in even steps
  const cum = new Float32Array(nSegs + 1);
  for (let s = 0; s < nSegs; s++) {
    const dx = segs[s * 4 + 2] - segs[s * 4], dz = segs[s * 4 + 3] - segs[s * 4 + 1];
    cum[s + 1] = cum[s] + Math.hypot(dx, dz);
  }
  const total = cum[nSegs] || 1;
  let s = 0;
  for (let i = 0; i < count; i++) {
    const t = ((i + 0.5) / count) * total;
    while (s < nSegs - 1 && cum[s + 1] < t) s++;
    const segLen = cum[s + 1] - cum[s] || 1;
    const f = (t - cum[s]) / segLen;
    const u = segs[s * 4] + (segs[s * 4 + 2] - segs[s * 4]) * f;
    const v = segs[s * 4 + 1] + (segs[s * 4 + 3] - segs[s * 4 + 1]) * f;
    p[i * 3] = u * PLATE_L * span;
    p[i * 3 + 1] = 0;
    p[i * 3 + 2] = v * PLATE_L * span;
    hc.setHSL(0.52 + Math.hypot(u, v) * 0.3, 0.75, 0.6);
    col[i * 3] = hc.r; col[i * 3 + 1] = hc.g; col[i * 3 + 2] = hc.b;
  }
  return { positions: p, colors: col };
}

/** Resolve a source to a point cloud of `count` points. Async for SVG/image. */
export async function buildCloud(source: Source, count: number, renderMode: RenderMode): Promise<Cloud> {
  if (source.kind === "formation") return FORMATIONS[source.key](count);
  if (source.kind === "text") return sampleText(source.text, count, source.font, renderMode);
  if (source.kind === "svg") return sampleSVG(source.svg, count, renderMode);
  if (source.kind === "lissajous") return lissajousCloud(count, source);
  if (source.kind === "cymatics") return cymaticsCloud(count, source);
  if (source.kind === "visualizer") return visualizerRestCloud(count);
  return sampleImageFile(source.url, count, renderMode);
}

/**
 * The visualizer's REST cloud — a thin ring. The live VisualizerField drives
 * its own positions from the analyser each frame; this placeholder is what
 * non-live paths (the sequencer cache, a physics field mid-switch) see.
 */
export function visualizerRestCloud(count: number): Cloud {
  const p = new Float32Array(count * 3);
  const col = new Float32Array(count * 3);
  const hc = new THREE.Color();
  for (let i = 0; i < count; i++) {
    const u = i / count;
    const a = u * TAU;
    p[i * 3] = Math.cos(a) * 3;
    p[i * 3 + 1] = Math.sin(a) * 3;
    p[i * 3 + 2] = 0;
    hc.setHSL(u, 0.8, 0.6);
    col[i * 3] = hc.r; col[i * 3 + 1] = hc.g; col[i * 3 + 2] = hc.b;
  }
  return { positions: p, colors: col };
}

/** A short human label for a source (scene cards, tooltips). */
export function sourceLabel(source: Source): string {
  switch (source.kind) {
    case "formation": return source.key;
    case "text": return `"${source.text}"`;
    case "svg": return source.name || "svg";
    case "image": return source.name || "image";
    case "lissajous": return `lissajous ${source.a}:${source.b}:${source.c}`;
    case "cymatics": return `cymatics ${source.m}:${source.n}${source.mix === 1 ? "+" : "−"}`;
    case "visualizer": return `visualizer · ${source.style}`;
  }
}
